import React from 'react';
import { CreditCard, Smartphone, Gift } from 'lucide-react';

/**
 * PaymentMethodSelector - Payment method selection cards
 * 
 * Features:
 * - Large selectable cards
 * - Clear active selection state
 * - Support for Card, QR, Gift Card
 */
export default function PaymentMethodSelector({
  selectedMethod = null, // 'card' | 'qr' | 'giftcard'
  onSelect,
  enableQR = true,
  enableGiftCard = false,
  className = '',
}) {
  const methods = [
    {
      id: 'card',
      label: 'Pay with Card',
      sublabel: 'Tap, insert, or swipe',
      icon: CreditCard,
      enabled: true,
    },
    {
      id: 'qr',
      label: 'Pay with Phone',
      sublabel: 'Scan QR code',
      icon: Smartphone,
      enabled: enableQR,
    },
    {
      id: 'giftcard',
      label: 'Gift Card',
      sublabel: 'Use balance',
      icon: Gift,
      enabled: enableGiftCard,
    },
  ].filter(m => m.enabled);

  return (
    <div className={`space-y-3 ${className}`}>
      <h3 className="text-lg font-semibold text-white mb-4">
        Payment Method
      </h3>

      <div className="space-y-3">
        {methods.map((method) => {
          const Icon = method.icon;
          const isSelected = selectedMethod === method.id;

          return (
            <button
              key={method.id}
              onClick={() => onSelect(method.id)}
              className={`
                w-full h-20 px-5
                flex items-center gap-4
                rounded-2xl border-2
                transition-all duration-150
                ${isSelected
                  ? 'bg-[#f5a623]/10 border-[#f5a623]'
                  : 'bg-[#1a1f26] border-white/10 hover:border-white/20 hover:bg-[#252b33]'
                }
                active:scale-[0.99]
              `}
              aria-pressed={isSelected}
            >
              <div className={`
                w-14 h-14 rounded-xl flex items-center justify-center
                ${isSelected ? 'bg-[#f5a623]/20' : 'bg-[#252b33]'}
              `}>
                <Icon 
                  size={28} 
                  className={isSelected ? 'text-[#f5a623]' : 'text-white'}
                />
              </div>
              <div className="flex-1 text-left">
                <p className={`text-lg font-semibold ${isSelected ? 'text-[#f5a623]' : 'text-white'}`}>
                  {method.label}
                </p>
                <p className={`text-sm ${isSelected ? 'text-[#f5a623]/70' : 'text-gray-500'}`}>
                  {method.sublabel}
                </p>
              </div>
              {/* Selection indicator */}
              <div className={`
                w-6 h-6 rounded-full border-2 flex items-center justify-center
                ${isSelected 
                  ? 'border-[#f5a623] bg-[#f5a623]' 
                  : 'border-white/30'
                }
              `}>
                {isSelected && (
                  <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                    <path d="M1 4L4.5 7.5L11 1" stroke="#0f1419" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
